import { useEffect, useState } from "react";
import { VideoCamera, X, Sparkle, ArrowsCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Auto-Deploy Cam On Vacancy
 * -----------------------------------------------------------------------
 * Opens when a Raza unit is marked vacant. Fetches ProCall spare cameras,
 * sorts them so the "most redeployable" ones float up (FLOAT-1, ST spares,
 * then oldest-deployed), and one-taps the chosen cam onto {address, unit}.
 *
 * Props:
 *   open           bool
 *   address        e.g. "5346 Chew Ave"
 *   unit           e.g. "8A"
 *   sourceProjectId (optional) — for audit trail
 *   onClose()
 *   onDeployed(payload)  — parent can refresh state / show downstream toast
 */
export default function AutoDeployCamModal({
  open, address, unit, sourceProjectId, onClose, onDeployed,
}) {
  const [cams, setCams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState("");   // system_code in flight

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/cameras/spares")
      .then(r => { if (!cancelled) setCams(r.data?.cameras || []); })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load spares"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const deploy = async (cam) => {
    setDeploying(cam.system_code);
    try {
      const r = await api.post(`/cameras/${encodeURIComponent(cam.system_code)}/deploy`, {
        address, unit, reason: "vacancy", source_project_id: sourceProjectId || null,
      });
      toast.success(`✅ ${cam.system_code} deployed to ${address}${unit ? ` · ${unit}` : ""}`,
        { description: cam.current_address ? `Moved from ${cam.current_address}` : "Was in floating inventory" });
      if (onDeployed) onDeployed(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Deploy failed");
    } finally {
      setDeploying("");
    }
  };

  if (!open) return null;

  const targetLabel = `${address}${unit ? ` · Unit ${unit}` : ""}`;

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="auto-deploy-cam-modal">
      <div className="bg-white w-full max-w-2xl border-4 border-indigo-700 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 bg-indigo-700 text-white flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <Sparkle weight="fill" size={13}/> Raza Vacancy · Redeploy Cam
            </div>
            <div className="font-display font-black text-lg mt-0.5 flex items-center gap-2">
              <VideoCamera weight="fill" size={22}/>
              Deploy a ProCall spare to
            </div>
            <div className="font-mono text-sm mt-0.5 truncate" data-testid="deploy-target-address">{targetLabel}</div>
          </div>
          <button onClick={onClose} data-testid="deploy-modal-close"
            className="shrink-0 h-9 w-9 flex items-center justify-center bg-white/10 hover:bg-white/20 border-2 border-white/40">
            <X weight="bold" size={16}/>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-slate-500 text-xs font-black uppercase tracking-widest">
              Loading spares…
            </div>
          ) : cams.length === 0 ? (
            <div className="py-8 text-center text-slate-600 text-sm">
              No ProCall spare cameras available. Add one via <span className="font-mono">/admin/assets</span>.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="deploy-cam-list">
              {cams.map(c => {
                const isFloat = (c.system_code || "").toUpperCase().startsWith("FLOAT")
                  || (c.system_code || "").toUpperCase().startsWith("ST");
                const isAtRaza = !!c.landlord_id;
                const dep = c.last_deployed_at ? new Date(c.last_deployed_at).toLocaleDateString() : "never";
                return (
                  <li key={c.system_code}
                    className={`p-3 border-2 flex items-center gap-3 ${
                      isFloat ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white"
                    }`}
                    data-testid={`deploy-cam-row-${c.system_code}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-black text-sm">{c.system_code}</span>
                        {isFloat && (
                          <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5">
                            Spare / Float
                          </span>
                        )}
                        {isAtRaza && !isFloat && (
                          <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5">
                            Currently at Raza
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 truncate">
                        {c.current_address || <span className="italic text-slate-400">not currently pinned</span>}
                        {c.current_unit ? ` · Unit ${c.current_unit}` : ""}
                      </div>
                      <div className="text-[10px] font-mono text-slate-400 mt-0.5 flex items-center gap-1">
                        <ArrowsCounterClockwise size={10}/> last moved: {dep}
                      </div>
                    </div>
                    <button
                      onClick={() => deploy(c)}
                      disabled={!!deploying}
                      data-testid={`deploy-cam-btn-${c.system_code}`}
                      className="shrink-0 h-10 px-3 font-black uppercase tracking-widest text-[11px] border-2 border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1">
                      {deploying === c.system_code ? "…" : "Deploy →"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 bg-slate-100 border-t-2 border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-between gap-3">
          <div>Silent — no SMS/email fires. Tap Deploy to stamp the fleet map.</div>
          <button onClick={onClose} data-testid="deploy-modal-skip"
            className="h-8 px-3 border-2 border-slate-800 bg-white hover:bg-slate-200">
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
